/**
 * Sprint 24.5 — NotifyMeButton.
 *
 * The CTA on a COMING SOON template card. Opens a small email-confirmation modal
 * and calls subscribe_template_notification (Q2). Idempotent server-side — a
 * repeat subscribe surfaces "You're already on the list." The email defaults to
 * the signed-in account; the user can edit it before confirming.
 */

import { useState, type CSSProperties } from "react";
import { showToast } from "../../primitives";
import { useAuth } from "../../../../lib/hooks/use-auth";
import { useSubscribeTemplateNotification } from "../../../../hooks/use-first-party-templates";

export interface NotifyMeButtonProps {
  templateSlug: string;
  templateName: string;
}

export function NotifyMeButton({ templateSlug, templateName }: NotifyMeButtonProps) {
  const { user } = useAuth();
  const subscribe = useSubscribeTemplateNotification();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email ?? "");

  async function confirm() {
    try {
      const result = await subscribe.mutateAsync({
        template_slug: templateSlug,
        email: email.trim() || undefined,
      });
      setOpen(false);
      if (result.already_subscribed) {
        showToast("You're already on the list.", "success");
      } else {
        showToast(`We'll notify you when ${templateName} launches.`, "success");
      }
    } catch {
      showToast("Couldn't save your subscription — try again.", "error");
    }
  }

  return (
    <>
      <button
        type="button"
        style={cta}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Notify me
      </button>

      {open && (
        <div
          style={backdrop}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
        >
          <div style={card} onClick={(e) => e.stopPropagation()}>
            <h3 style={h3}>Get notified about {templateName}</h3>
            <p style={sub}>
              We'll email you once {templateName} is ready to connect. No spam — just the launch.
            </p>
            <label style={field}>
              <span style={labelText}>Email</span>
              <input
                style={input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <div style={footer}>
              <button type="button" style={cancelBtn} onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                style={primaryBtn}
                disabled={subscribe.isPending}
                onClick={confirm}
              >
                {subscribe.isPending ? "Saving…" : "Notify me"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const cta: CSSProperties = {
  fontSize: 11,
  padding: "6px 14px",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-tertiary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-sm)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 110,
};

const card: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  width: 380,
  maxWidth: "90vw",
  padding: "20px 22px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  fontFamily: "var(--font-sans)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
};

const h3: CSSProperties = { fontSize: 15, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const sub: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)", margin: 0, lineHeight: 1.5 };

const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelText: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)" };
const input: CSSProperties = {
  fontSize: 13,
  padding: "8px 10px",
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const footer: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 };

const cancelBtn: CSSProperties = {
  padding: "6px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "6px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
